import { useState, useEffect } from "react";
import { Home, Search, ArrowLeft, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function NotFound() {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  // Track mouse position for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#FFF4F0] via-white to-[#FFE5DC]">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Circles */}
        <div
          className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-gradient-to-br from-[#F4A696]/20 to-[#E89580]/10 blur-3xl animate-float"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            transition: "transform 0.3s ease-out",
          }}
        />
        <div
          className="absolute top-1/3 -right-32 h-96 w-96 rounded-full bg-gradient-to-br from-[#E89580]/20 to-[#F4A696]/10 blur-3xl animate-float-delayed"
          style={{
            transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`,
            transition: "transform 0.3s ease-out",
          }}
        />
        <div
          className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-gradient-to-br from-[#FFE5DC]/30 to-[#F4A696]/20 blur-3xl animate-float-slow"
          style={{
            transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`,
            transition: "transform 0.3s ease-out",
          }}
        />

        {/* Animated Event Icons */}
        <div className="absolute top-20 left-10 animate-bounce-slow opacity-20">
          <Calendar className="h-12 w-12 text-[#F4A696]" />
        </div>
        <div className="absolute bottom-32 right-20 animate-bounce-delayed opacity-20">
          <Calendar className="h-16 w-16 text-[#E89580]" />
        </div>
        <div className="absolute top-1/2 right-10 animate-spin-slow opacity-20">
          <Calendar className="h-10 w-10 text-[#F4A696]" />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex min-h-screen flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Animated 404 Text */}
          <div className="relative mb-8">
            <h1
              className="text-[180px] font-black leading-none text-transparent bg-clip-text bg-gradient-to-r from-[#F4A696] via-[#E89580] to-[#F4A696] animate-gradient-x sm:text-[250px] select-none"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              style={{
                transform: isHovering ? "scale(1.05)" : "scale(1)",
                transition: "transform 0.3s ease",
              }}
            >
              404
            </h1>
            
            {/* Floating Dots */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 flex gap-4">
              <div className="h-3 w-3 rounded-full bg-[#F4A696] animate-bounce" />
              <div className="h-3 w-3 rounded-full bg-[#E89580] animate-bounce animation-delay-200" />
              <div className="h-3 w-3 rounded-full bg-[#F4A696] animate-bounce animation-delay-400" />
            </div>
          </div>

          {/* Event House Logo Animation */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 animate-ping rounded-full bg-[#F4A696] opacity-20" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#F4A696] to-[#E89580] shadow-xl">
                <Calendar className="h-10 w-10 text-white animate-pulse" />
              </div>
            </div>
          </div>

          {/* Error Message */}
          <h2 className="mb-4 text-4xl font-bold text-[#1B3C5C] sm:text-5xl animate-fade-in-up">
            Oops! Event Not Found
          </h2>
          <p className="mb-8 text-lg text-[#1B3C5C]/70 max-w-md mx-auto animate-fade-in-up animation-delay-200">
            Looks like this page took an unexpected detour. Don't worry, we'll
            help you get back to the party! 🎉
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center animate-fade-in-up animation-delay-400">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-[#F4A696] font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-[#F4A696]/20"
            >
              <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
              Go Back
            </button>

            <button
              onClick={() => navigate("/")}
              className="group flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F4A696] to-[#E89580] px-8 py-4 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <Home className="h-5 w-5 transition-transform group-hover:scale-110" />
              Back to Home
            </button>
          </div>

          {/* Quick Links */}
          <div className="mt-12 animate-fade-in-up animation-delay-600">
            <p className="mb-4 text-sm font-medium text-[#1B3C5C]/60">
              Popular destinations:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: "Events", path: "/event" },
                { label: "Dashboard", path: "/dashboard" },
                { label: "Contact", path: "/contact" },
              ].map((link, index) => (
                <button
                  key={index}
                  onClick={() => navigate(link.path)}
                  className="rounded-full bg-[#FFF4F0] px-6 py-2 text-sm font-medium text-[#F4A696] transition-all hover:bg-[#F4A696] hover:text-white hover:-translate-y-0.5 hover:shadow-md"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-8 animate-fade-in-up animation-delay-800">
            <div className="relative mx-auto max-w-md">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
              <input
                type="search"
                placeholder="Search for events..."
                className="w-full rounded-full border-2 border-[#F4A696]/30 bg-white py-3 pl-12 pr-4 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-4 focus:ring-[#F4A696]/20 transition-all"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    navigate("/event");
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer Note */}
      
      </div>
    </div>
  );
}
